import { useState } from 'react'
import { useQuery } from '@truepill/tpos-react-router'
import QueuesSummary from 'components/QueuesSummary'
import { gql } from 'gql/generated'
import useErrorToast from 'hooks/toast/useErrorToast'
import useIsOtcOnlyUser from 'hooks/useIsOtcOnlyUser'
import moment from 'moment'
import { usePlusClient } from 'providers/VisionRouter'

const GET_OTC_ONLY_ORDERS = gql(`
query getOtcOnlyOrders($locationIds: [ID], $customerIds: [ID], $states: [String]) {
  getOtcOnlyOrders(locationIds: $locationIds, customerIds: $customerIds, states: $states) {
    Packing
  }
}
`)

const GET_FILLS_BY_STATUS = gql(`
query getFillsByStatus($locationIds: [ID], $customerIds: [ID], $states: [String]) {
  getFillsByStatus(locationIds: $locationIds, customerIds: $customerIds, states: $states) {
    PV1 {
      total
      between12And24Hours
      greaterThan24Hours
    }
    Fill {
      total
      between12And24Hours
      greaterThan24Hours
    }
    Automation {
      total
      between12And24Hours
      greaterThan24Hours
    }
    PV2 {
      total
      between12And24Hours
      greaterThan24Hours
    }
    Adjudication {
      total
      between12And24Hours
      greaterThan24Hours
    }
    PriorAuthorization {
      total
      between12And24Hours
      greaterThan24Hours
    }
  }
}
`)

const GET_ORDERS_BY_STATUS = gql(`
query getOrdersByStatus($locationIds: [ID], $customerIds: [ID], $states: [String]) {
  getOrdersByStatus(locationIds: $locationIds, customerIds: $customerIds, states: $states) {
    packing
  }
}
`)

const GET_TRIAGES = gql(`
query getTriages($locationIds: [ID], $customerIds: [ID], $states: [String]) {
  getTriages(locationIds: $locationIds, customerIds: $customerIds, states: $states) {
    inTriage
  }
}
`)

const GET_TRANSFERS = gql(`
  query getTransfers($locationIds: [ID], $customerIds: [ID]) {
    getTransfers(locationIds: $locationIds, customerIds: $customerIds) {
      FaxFailed
      ToTransfer
      NeedsVerification
    }
  }
`)

const getCurrentTime = () => moment().format('MM/DD/YYYY hh:mm:ssA')

const StatsSection = (): JSX.Element => {
  const [lastFetchTime, setLastFetchTime] = useState(getCurrentTime())
  const {
    currentLocation: { queryMap },
  } = usePlusClient()
  const showErrorToast = useErrorToast()
  const isOtcOnlyUser = useIsOtcOnlyUser()

  const { data: otcOnlyOrders } = useQuery(GET_OTC_ONLY_ORDERS, {
    variables: {
      locationIds: queryMap.locationIds,
      customerIds: queryMap.customerIds,
      states: queryMap.states,
    },
    onCompleted: () => setLastFetchTime(getCurrentTime()),
    onError: error => showErrorToast(error.message),
    fetchPolicy: 'cache-and-network',
    pollInterval: 180_000,
  })

  const { data: ordersByStatus } = useQuery(GET_ORDERS_BY_STATUS, {
    variables: {
      locationIds: queryMap.locationIds,
      customerIds: queryMap.customerIds,
      states: queryMap.states,
    },
    skip: isOtcOnlyUser,
    onCompleted: () => setLastFetchTime(getCurrentTime()),
    onError: error => showErrorToast(error.message),
    fetchPolicy: 'cache-and-network',
    pollInterval: 180_000,
  })

  const { data: fillsByStatus = { getFillsByStatus: null } } = useQuery(GET_FILLS_BY_STATUS, {
    variables: {
      locationIds: queryMap.locationIds,
      customerIds: queryMap.customerIds,
      states: queryMap.states,
    },
    skip: isOtcOnlyUser,
    onCompleted: () => setLastFetchTime(getCurrentTime()),
    onError: error => showErrorToast(error.message),
    fetchPolicy: 'cache-and-network',
    pollInterval: 30_000,
  })

  const { data: triages } = useQuery(GET_TRIAGES, {
    variables: {
      locationIds: queryMap.locationIds,
      customerIds: queryMap.customerIds,
      states: queryMap.states,
    },
    onCompleted: () => setLastFetchTime(getCurrentTime()),
    onError: error => showErrorToast(error.message),
    fetchPolicy: 'cache-and-network',
    pollInterval: 180_000,
  })

  const { data: transfers } = useQuery(GET_TRANSFERS, {
    variables: {
      locationIds: queryMap.locationIds,
      customerIds: queryMap.customerIds,
    },
    skip: isOtcOnlyUser,
    onCompleted: () => setLastFetchTime(getCurrentTime()),
    onError: error => showErrorToast(error.message),
    fetchPolicy: 'cache-and-network',
    pollInterval: 180_000,
  })

  return (
    <QueuesSummary
      lastFetchTime={lastFetchTime}
      fillsByStatus={fillsByStatus.getFillsByStatus}
      triage={
        triages?.getTriages
          ? { total: triages.getTriages.inTriage, between12And24Hours: null, greaterThan24Hours: null }
          : null
      }
      packing={
        ordersByStatus?.getOrdersByStatus
          ? { total: ordersByStatus.getOrdersByStatus.packing, between12And24Hours: null, greaterThan24Hours: null }
          : null
      }
      otcOnly={
        otcOnlyOrders?.getOtcOnlyOrders
          ? { total: otcOnlyOrders.getOtcOnlyOrders.Packing, between12And24Hours: null, greaterThan24Hours: null }
          : null
      }
      transfers={
        transfers?.getTransfers
          ? {
              FaxFailed: transfers.getTransfers.FaxFailed,
              ToTransfer: transfers.getTransfers.ToTransfer,
              NeedsVerification: transfers.getTransfers.NeedsVerification,
            }
          : null
      }
    />
  )
}

export default StatsSection
